import React from 'react';

import uniqBy from 'lodash/uniqBy';

import { CircularProgress } from '@mui/material';

import Separator from 'ui/Feed/Separator';
import { ActivityType } from 'utils/stores/Activities/constants';

import { Button } from 'components/Button';

import { useVirtualRows } from './useVirtualRows';
import VirtualRow from './VirtualRow';
import { LoadMore, ScrollArea, Spacer } from './styles';

type FeedRow<T> = { key: string; day: string; entry?: T };

type HistoryFeedProps<T> = {
  days: [string, T[]][];
  getKey: (entry: T) => string;
  renderEntry: (entry: T) => React.ReactNode;
  hasMore: boolean;
  loadingMore?: boolean;
  onLoadMore: () => void;
  loadMoreTestId?: string;
};

const HistoryFeed = <T,>({
  days,
  getKey,
  renderEntry,
  hasMore,
  loadingMore = false,
  onLoadMore,
  loadMoreTestId,
}: HistoryFeedProps<T>): React.ReactElement => {
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const rows: FeedRow<T>[] = days.flatMap(([day, entries]) => [
    { key: `day-${day}`, day },
    ...uniqBy(entries, getKey).map((entry) => ({ key: getKey(entry), day, entry })),
  ]);

  const { start, end, paddingTop, paddingBottom, observer } = useVirtualRows(
    rows.map(({ key }) => key),
    scrollRef
  );

  return (
    <>
      {hasMore && (
        <LoadMore>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            disabled={loadingMore}
            data-testid={loadMoreTestId}
            onClick={onLoadMore}
          >
            {loadingMore ? <CircularProgress size={20} /> : 'Load more'}
          </Button>
        </LoadMore>
      )}
      <ScrollArea ref={scrollRef} data-testid="history-feed-scroll">
        <Spacer $top={paddingTop} $bottom={paddingBottom}>
          {rows.slice(start, end + 1).map((row) => (
            <VirtualRow key={row.key} rowKey={row.key} observer={observer}>
              {row.entry === undefined ? (
                <Separator activity={{ created: row.day, type: ActivityType.Separator, uuid: row.day }} />
              ) : (
                renderEntry(row.entry)
              )}
            </VirtualRow>
          ))}
        </Spacer>
      </ScrollArea>
    </>
  );
};

export default HistoryFeed;
